import { cn } from '@/lib/utils';
import type { ToolUIPart } from 'ai';
import { Tool, ToolContent, ToolHeader, ToolInput, ToolOutput } from './tool';

/**
 * Una tool call d'un torn de Miranda, tal com el backend la desa a `chat_messages.tool_calls`
 * (`apps/backend/src/modules/agent/turn-record.ts` → `RecordedToolCall`).
 *
 * `args` i `result` són `unknown` a posta: si el JSON passava de 4 KB, el backend hi desa el
 * text retallat en comptes de l'objecte i marca `truncated`.
 */
export interface ToolCallRecord {
  name: string;
  args?: unknown;
  result?: unknown;
  error?: string;
  truncated?: boolean;
}

export interface ToolCallCardProps {
  call: ToolCallRecord;
  /** Plegada per defecte: el xat ha de llegir-se sense JSON a la vista. */
  defaultOpen?: boolean;
  className?: string;
}

/** Resultat d'una tool que ha dit «no» sense llançar: el patró `{ success: false, message }`. */
function isFailedResult(value: unknown): value is { success: false; message?: unknown } {
  return typeof value === 'object' && value !== null && 'success' in value && value.success === false;
}

interface ToolCallState {
  state: ToolUIPart['state'];
  /** Missatge a pintar en vermell; el JSON sencer segueix anant a `ToolOutput` com a `output`. */
  errorText?: string;
}

/**
 * Estat de la targeta a partir del que hi ha desat. Els estats són els d'`ToolUIPart` de l'AI SDK
 * perquè és el que entén `ToolHeader`, però aquí no vénen d'un stream: vénen de la DB.
 *
 * Dues menes de fallada:
 *  - La tool ha llançat. El backend (`turn-record.ts`) ho desa a `error` (part `tool-error`).
 *  - La tool ha retornat `{ success: false, message }`, que és com fallen gairebé totes les de
 *    Miranda. Sense això sortiria amb la marca verda de «Feta».
 *
 * Una crida sense resultat ni error no és cap anomalia — el `tool_calls` es desa quan acaba el
 * torn, i mentre el torn corre (o a les files velles d'abans del NOV-2025) el resultat no hi és.
 */
function toolState(call: ToolCallRecord): ToolCallState {
  if (call.error) return { state: 'output-error', errorText: call.error };
  if (isFailedResult(call.result)) {
    return {
      state: 'output-error',
      errorText:
        typeof call.result.message === 'string'
          ? call.result.message
          : 'La tool ha retornat success: false',
    };
  }
  if (call.result !== undefined) return { state: 'output-available' };
  return { state: 'input-available' };
}

/**
 * Targeta d'una tool call al xat (bug #5 de `CURRENT.md`).
 *
 * Substitueix els dos `<pre>{JSON.stringify(...)}</pre>` que `ChatPage` pintava sempre oberts i
 * sempre amb una marca verda, tant si la tool havia anat bé com si havia petat. Ara: un estat
 * real (feta, error, en curs), el JSON dins d'un `CodeBlock` plegat, i l'error en vermell amb el
 * seu missatge.
 */
export function ToolCallCard({ call, defaultOpen = false, className }: ToolCallCardProps) {
  const { state, errorText } = toolState(call);
  const hasArgs = call.args !== undefined && call.args !== null;

  return (
    <Tool className={cn('mb-0 bg-background/50', className)} defaultOpen={defaultOpen}>
      <ToolHeader
        title={call.name}
        // `ToolHeader` espera el `type` d'una part de l'AI SDK (`tool-<nom>`); amb `title` posat
        // només el fa servir si no hi ha títol, però el tipus el demana igualment.
        type={`tool-${call.name}`}
        state={state}
      />
      <ToolContent>
        {hasArgs && <ToolInput input={call.args} />}
        <ToolOutput output={call.result} errorText={errorText} />
        {call.truncated && (
          <p className="px-4 pb-3 text-muted-foreground text-xs">
            Contingut retallat: el backend només en desa els primers 4 KB.
          </p>
        )}
      </ToolContent>
    </Tool>
  );
}

export default ToolCallCard;
